import { useEffect, useRef, type CSSProperties } from 'react';
import { useVisualViewport } from '@ev-tech/search-modules-shared/VisualViewport';
import { BreakpointProvider, useBreakpoint } from '@ev-tech/search-modules-shared/BreakpointProvider';
import { Dialog } from '@base-ui/react/dialog';

import { ChatbotLauncher } from '../ChatbotLauncher/ChatbotLauncher';
import { ChatbotHeader } from '../ChatbotHeader/ChatbotHeader';
import { ChatbotConversation } from '../ChatbotConversation/ChatbotConversation';
import { ChatbotComposer } from '../ChatbotComposer/ChatbotComposer';
import type { PageSkill } from '../../types';
import { useChatbotStore } from '../../stores/useChatbot';
import { useChatbotSession } from '../../providers/ChatbotProvider';
import { DEFAULT_SUGGESTIONS } from '../../contants/suggestions';

import styles from './Chatbot.module.css';

export type ChatbotProps = {
  pageSkills?: PageSkill[];
  suggestions?: string[];
  className?: string;
};

const ChatbotContent = ({ pageSkills = [], suggestions, className }: ChatbotProps) => {
  const open = useChatbotStore((state) => state.open);
  const expanded = useChatbotStore((state) => state.expanded);
  const setOpen = useChatbotStore((state) => state.setOpen);
  const setExpanded = useChatbotStore((state) => state.setExpanded);
  const registerPageSkill = useChatbotStore((state) => state.registerPageSkill);
  const { t, sendMessage, newChat, isPending, isConfirming } = useChatbotSession();
  const prompts = suggestions ?? DEFAULT_SUGGESTIONS.map((suggestion) => t(suggestion));
  const { isMobile } = useBreakpoint();
  const viewport = useVisualViewport(open && isMobile);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const shouldFollowRef = useRef(true);

  useEffect(() => {
    // call registerPageSkill for each page skill and store the unregister functions in an array
    const unregisterFunctions = pageSkills.map(registerPageSkill);

    return () => {
      for (const unregister of unregisterFunctions) unregister();
    };
  }, [pageSkills, registerPageSkill]);

  const submit = (message: string) => {
    if (!isPending && !isConfirming) {
      shouldFollowRef.current = true;
      sendMessage(message);
    }
  };

  const startNewChat = () => {
    newChat();
    shouldFollowRef.current = true;
    inputRef.current?.focus();
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen} modal={isMobile} disablePointerDismissal>
      <ChatbotLauncher open={open} className={className} />
      <Dialog.Portal>
        <Dialog.Viewport className={styles.viewport}>
          <Dialog.Popup
            className={styles.window}
            data-expanded={expanded}
            data-testid="chatbot-window"
            aria-modal={isMobile ? true : undefined}
            style={
              {
                '--chatbot-viewport-height': viewport.height ? `${viewport.height}px` : '100dvh',
                '--chatbot-viewport-top': `${viewport.offsetTop}px`,
              } as CSSProperties
            }
            initialFocus={(type) => (type === 'touch' ? true : inputRef.current)}
          >
            <ChatbotHeader mobile={isMobile} expanded={expanded} onNewChat={startNewChat} onToggleExpanded={() => setExpanded(!expanded)} />
            <div className={styles.body}>
              <ChatbotConversation
                suggestions={prompts}
                isPending={isPending}
                open={open}
                shouldFollowRef={shouldFollowRef}
                onSendMessage={submit}
                onRetry={() => sendMessage('', { retry: true })}
              />
              <ChatbotComposer isPending={isPending || isConfirming} inputRef={inputRef} onSendMessage={submit} />
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export const Chatbot = (props: ChatbotProps) => (
  <BreakpointProvider>
    <ChatbotContent {...props} />
  </BreakpointProvider>
);
